import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Spinner,
  Typography,
} from "@material-tailwind/react";

import AlertMessage from "../../common/actions/AlertMessage";
import OpenModal from "../../common/actions/OpenModal";
import Loader from "../../common/loader/Loader";
import DetailItem from "../../common/shared/DetailItem";
import NoContent from "../../common/shared/NoContent";
import SectionTitle from "../../common/shared/SectionTitle";
import { getYlfProducts, registerYlf } from "../../redux/actions/ylf_action";
import { Chip } from "@material-tailwind/react";
import { openZoomLink } from "../../utils/zoomOpen";
import eligibleYlfBarNumbers from "./eligibleYlfBarNumbers.json";

const links = [{ name: "Young Lawyers Forum" }];
const eligibleYlfBarNumberSet = new Set(eligibleYlfBarNumbers);

const getProductMeta = (product) => {
  if (!product?.meta || typeof product.meta !== "string") {
    return product?.meta || {};
  }

  try {
    return JSON.parse(product.meta);
  } catch (error) {
    return {};
  }
};

const getZoomLink = (meta) =>
  meta?.zoom || meta?.zoom_link || meta?.zoomLink || meta?.zoom_url || "";

const normalizeBarNumber = (barNumber) => {
  if (barNumber === null || barNumber === undefined) {
    return "";
  }

  return String(barNumber).trim().replace(/\.0$/, "");
};

const isEligibleYlfMember = (barNumber) => {
  const normalizedBarNumber = normalizeBarNumber(barNumber);

  if (!normalizedBarNumber) {
    return false;
  }

  return eligibleYlfBarNumberSet.has(normalizedBarNumber);
};

function YoungLawyersForum() {
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [virtualRegistering, setVirtualRegistering] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [attendanceMode, setAttendanceMode] = useState("");
  const [validationError, setValidationError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const dispatch = useDispatch();
  const { products, ylfError, message } = useSelector((state) => state.ylf);
  const userData = useSelector((state) => state.auth?.userData);
  const storedUser = JSON.parse(localStorage.getItem("profile"))?.user;
  const user = userData || storedUser;

  const product = products?.[0];
  const meta = getProductMeta(product);
  const zoomLink = getZoomLink(meta);
  const isRegistered = Boolean(product?.registered || product?.registration);
  const canAccessZoomLink =
    isRegistered || isEligibleYlfMember(user?.bar_number);
  const savedAttendanceMode =
    product?.registration?.attendance_mode ||
    product?.registration?.registration_data?.attendance_mode;

  useEffect(() => {
    dispatch(getYlfProducts()).finally(() => setLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (savedAttendanceMode) {
      setAttendanceMode(savedAttendanceMode);
    }
  }, [savedAttendanceMode]);

  const registrationOpen = !meta.pause || Date.parse(meta.pause) > Date.now();

  const handleModal = (mode) => {
    setValidationError("");
    setAttendanceMode(mode);
    setShowModal((prev) => !prev);
  };

  const handleRegister = async (mode) => {
    setValidationError("");
    setSuccessMessage("");

    if (!mode) {
      setValidationError("Please select an attendance mode.");
      return;
    }

    if (!product?.id) {
      setValidationError("Young Lawyers Forum product is not available.");
      return;
    }

    if (mode === "In-person") {
      setRegistering(true);
    } else {
      setVirtualRegistering(true);
    }

    const response = await dispatch(
      registerYlf({
        id: product.id,
        product_id: product.id,
        post_type: "ylf",
        attendance_mode: mode,
      }),
    );

    setRegistering(false);
    setVirtualRegistering(false);
    setShowModal(false);

    if (response?.error) {
      return;
    }

    setSuccessMessage(
      response?.data?.message ||
        message ||
        "Young Lawyers Forum registration completed successfully.",
    );
  };

  return (
    <>
      <SectionTitle
        title="Young Lawyers Forum"
        description="Register for the Young Lawyers Forum."
        children={links}
        link="/home"
      />

      <div className="my-8 max-w-[1200px] m-auto px-4">
        {ylfError && <AlertMessage type="error" message={ylfError} />}
        {validationError && (
          <AlertMessage type="warning" message={validationError} />
        )}
        {successMessage && (
          <AlertMessage type="success" message={successMessage} />
        )}

        {loading ? (
          <Loader />
        ) : !product ? (
          <NoContent message="Young Lawyers Forum registration is not available at the moment." />
        ) : (
          <div className="card-wrapper">
            {!isRegistered && registrationOpen && (
              <div className="flex flex-col gap-4 md:flex-row md:items-center mb-2">
                <Button
                  color="green"
                  onClick={() => handleModal("In-person")}
                  fullWidth
                  size="lg"
                  disabled={registering || virtualRegistering}
                >
                  {registering ? (
                    <Spinner color="white" />
                  ) : (
                    <>
                      Click Here for <span className="text-3xl">In-person</span>{" "}
                      Participation
                    </>
                  )}
                </Button>

                <Button
                  color="light-blue"
                  onClick={() => handleModal("Virtual")}
                  fullWidth
                  disabled={registering || virtualRegistering}
                >
                  {virtualRegistering ? (
                    <Spinner color="white" />
                  ) : (
                    <>
                      Click Here for <span className="text-3xl">Virtual</span>{" "}
                      Participation
                    </>
                  )}
                </Button>
              </div>
            )}

            {!isRegistered && !registrationOpen && (
              <AlertMessage
                type="warning"
                message="Young Lawyers Forum registration is closed."
              />
            )}

            <Card>
              <CardBody>
                <div className="flex mb-2">
                  {!isRegistered ? (
                    <Chip
                      color="light-blue"
                      variant="ghost"
                      value={`NOT REGISTERED`}
                    />
                  ) : (
                    <Chip color="green" variant="ghost" value={`Registered`} />
                  )}
                </div>

                <Typography variant="h5" color="blue-gray" className="mb-4">
                  {`Young Lawyers Forum`}
                </Typography>

                <DetailItem title="Date" item={`Tuesday, 7th July, 2026`} />

                <DetailItem title="Time" item={`12pm`} />

                <DetailItem title="Venue" item={`Alisa Hotel, North Ridge`} />
              </CardBody>

              <CardFooter>
                {canAccessZoomLink && zoomLink && (
                  <Button color="blue" onClick={() => openZoomLink(zoomLink)}>
                    Zoom Link
                  </Button>
                )}
              </CardFooter>
            </Card>

            {showModal && (
              <OpenModal
                open={showModal}
                handler={() => handleModal(attendanceMode)}
                title="Confirm Participation"
                loading={registering || virtualRegistering}
                actionTitle="Proceed"
                handleSubmit={() => handleRegister(attendanceMode)}
                disable={registering || virtualRegistering}
                children={
                  <div className="flex flex-col gap-4">
                    <Typography color="black" className="text-xl">
                      Please confirm{" "}
                      <span className="font-bold">{attendanceMode}</span>{" "}
                      Participation?
                    </Typography>
                  </div>
                }
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}

export default YoungLawyersForum;
